import React, { useState } from 'react';
import { MdClose } from 'react-icons/md';

interface MipSyncModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => Promise<void>;
    startDate: string;
    endDate: string;
}

const MipSyncModal: React.FC<MipSyncModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    startDate,
    endDate,
}) => {
    const [isProcessing, setIsProcessing] = useState(false);

    // Parse and format the dates properly to avoid timezone issues
    const formatDisplayDate = (dateString: string) => {
        if (!dateString) return '';
        
        // Parse the date string as local date (not UTC)
        const [year, month, day] = dateString.split('-').map(Number);
        const date = new Date(year, month - 1, day);
        
        // Format for display (mm/dd/yyyy)
        const displayMonth = (date.getMonth() + 1).toString().padStart(2, '0');
        const displayDay = date.getDate().toString().padStart(2, '0');
        const displayYear = date.getFullYear();
        
        return `${displayMonth}/${displayDay}/${displayYear}`;
    };

    const handleConfirm = async () => {
        setIsProcessing(true);
        try {
            await onConfirm();
        } finally {
            setIsProcessing(false);
            onClose();
        }
    };

    if (!isOpen) return null;

    const formattedStartDate = formatDisplayDate(startDate);
    const formattedEndDate = formatDisplayDate(endDate);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="w-full max-w-lg rounded-lg bg-white p-6 dark:bg-boxdark">
                {/* Header */}
                <div className="mb-6 flex items-center justify-between">
                    <h3 className="text-xl font-medium text-black dark:text-white">
                        Confirm MIP Sync
                    </h3>
                    {!isProcessing && (
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                        >
                            <MdClose size={24} />
                        </button>
                    )}
                </div>

                {/* Content */}
                <div className="mb-6">
                    <div className="mb-4 rounded-lg bg-blue-50 p-4 dark:bg-blue-900/20">
                        <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
                            Date Range:
                        </p>
                        <p className="mt-1 text-lg font-semibold text-blue-900 dark:text-blue-100">
                            {formattedStartDate} to {formattedEndDate}
                        </p>
                    </div>

                    <div className="space-y-3 text-sm text-gray-600 dark:text-gray-400">
                        <p className="font-medium text-black dark:text-white">
                            This sync will:
                        </p>
                        <ul className="list-disc space-y-1 pl-5">
                            <li>Process disbursement data from MIP sequentially (one record at a time)</li>
                            <li>Block the UI while processing (may take several minutes)</li>
                            <li>Show results when complete</li>
                        </ul>
                        <p className="mt-3 text-xs italic">
                            Processing time depends on the date range size.
                        </p>
                    </div>
                </div>

                {/* Processing Indicator */}
                {isProcessing && (
                    <div className="mb-6 rounded-lg bg-yellow-50 p-4 dark:bg-yellow-900/20">
                        <div className="flex items-center gap-3">
                            <div className="h-5 w-5 animate-spin rounded-full border-2 border-yellow-600 border-t-transparent"></div>
                            <div>
                                <p className="font-medium text-yellow-800 dark:text-yellow-200">
                                    Processing MIP Sync...
                                </p>
                                <p className="text-sm text-yellow-700 dark:text-yellow-300">
                                    Please wait, this may take several minutes
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Actions */}
                <div className="flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        disabled={isProcessing}
                        className="rounded border border-stroke px-4 py-2 text-black hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-strokedark dark:text-white dark:hover:bg-meta-4"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleConfirm}
                        disabled={isProcessing}
                        className="rounded bg-primary px-4 py-2 text-white hover:bg-opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        {isProcessing ? 'Processing...' : 'Confirm Sync'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default MipSyncModal;